import React, { useEffect } from 'react';
import { InfoMessage } from 'features/components/InfoMessage';
import { Switch } from 'features/components/Switch';
import { PermissionsState, permissionDependencies } from '../hooks/usePermissions';
import { useSelector } from 'hooks/utils';
import { LanguageType } from 'types/index';
import { ModulesPermissions } from './ModulesPermissionsSection';
import { useIsCodelistEnabledForProject } from 'features/codelist/useIsCodeListEnabled';

const DICTIONARY = {
	[LanguageType.English]: {
		permissions: 'Permissions',
		generalPermissions: 'General Permissions',
		additionalPermissions: 'Additional permissions',
		view: 'View',
		edit: 'Edit',
		analyse: 'Analyse',
		delete: 'Delete',
		ownData: 'Own data',
		groupData: 'Group data',
		allData: 'All data',
		allowExport: 'Allow export of data',
		allowRestricted: 'Allow access to "restricted" variables',
		allowEditVariables: 'Allow editing of project design',
		allowEditProject: 'Allow editing of project information',
		canModifyLater: 'Permissions can be modified later for each user.',
		roleControlled:
			'These permissions are controlled by the selected role and cannot be modified.',
		roleControlledItem: 'Controlled by role',
		allowCodelistRead: 'Allow reading of codelists',
		allowCodelistWrite: 'Allow writing to codelists',
		allowCodelistExport: 'Allow exporting codelists'
	},
	[LanguageType.Norwegian]: {
		permissions: 'Tillatelser',
		generalPermissions: 'Generelle tillatelser',
		additionalPermissions: 'Ytterligere tillatelser',
		view: 'Vis',
		edit: 'Rediger',
		analyse: 'Analyser',
		delete: 'Slett',
		ownData: 'Egne data',
		groupData: 'Gruppedata',
		allData: 'Alle data',
		allowExport: 'Tillat eksport av data',
		allowRestricted: 'Tillat tilgang til "begrensede" variabler',
		allowEditVariables: 'Tillat redigering av prosjektdesign',
		allowEditProject: 'Tillat redigering av prosjektinformasjon',
		canModifyLater: 'Tillatelser kan endres senere for hver bruker.',
		roleControlled: 'Disse tillatelsene styres av den valgte rollen og kan ikke endres.',
		roleControlledItem: 'Styrt av rolle',
		allowCodelistRead: 'Tillat lesing av kodelister',
		allowCodelistWrite: 'Tillat skriving til kodelister',
		allowCodelistExport: 'Tillat eksport av kodelister'
	}
};

interface PermissionsEditorProps {
	permissions: PermissionsState;
	onUpdatePermission: (flag: keyof PermissionsState) => void;
	isRoleControlled?: boolean;
	hasOrganizationExportAccess: boolean;
	updateModules: (modules: ModulesPermissions) => void;
}

type PermissionLevel = 'Own' | 'Org' | 'All';
type PermissionType = 'Read' | 'Write' | 'Delete' | 'Analysis';

const getFlagKey = (level: PermissionLevel, type: PermissionType): keyof PermissionsState => {
	const levelPrefix = level === 'Own' ? 'Ownonly' : level;
	if (type === 'Analysis') {
		return `accessAnalysis${levelPrefix}` as keyof PermissionsState;
	} else {
		return `accessEntries${levelPrefix}${type}` as keyof PermissionsState;
	}
};

export const PermissionsEditor: React.FC<PermissionsEditorProps> = ({
	permissions,
	onUpdatePermission,
	hasOrganizationExportAccess,
	updateModules,
	isRoleControlled = false
}) => {
	const language = useSelector(state => state.ui.i18n.language);
	const dictionary = DICTIONARY[language];
	const isCodelistEnabledForProject = useIsCodelistEnabledForProject();

	const permissionHeaders: { header: string; type: PermissionType }[] = [
		{ header: dictionary.view, type: 'Read' },
		{ header: dictionary.edit, type: 'Write' },
		{ header: dictionary.analyse, type: 'Analysis' },
		{ header: dictionary.delete, type: 'Delete' }
	];

	const dataLevels: { level: PermissionLevel; labelKey: string }[] = [
		{ level: 'Own', labelKey: dictionary.ownData },
		{ level: 'Org', labelKey: dictionary.groupData },
		{ level: 'All', labelKey: dictionary.allData }
	];

	const isSwitchDisabled = (targetFlag: keyof PermissionsState): boolean => {
		if (isRoleControlled) return true;

		// Special case for export permission, only allowed if the user has access to delegate export access
		if (targetFlag === 'accessExport') {
			return !hasOrganizationExportAccess;
		}

		// Check if any other permission that disables this one is true
		for (const sourceFlag in permissionDependencies) {
			if (permissions[sourceFlag as keyof PermissionsState]) {
				const rule = permissionDependencies[sourceFlag as keyof PermissionsState];
				if (rule?.disablesOnTrue?.includes(targetFlag)) {
					return true;
				}
			}
		}
		return false;
	};

	const handlePermissionChange = (flag: keyof PermissionsState) => {
		if (isRoleControlled) return;
		onUpdatePermission(flag);
	};

	useEffect(() => {
		updateModules({
			projectDesign: permissions.accessVariablesWrite,
			collaborators: permissions.accessProjectWrite
		});
	}, [permissions, updateModules]);

	return (
		<div>
			<h2 className="text-2xl font-bold text-gray-800 mb-6">{dictionary.permissions}</h2>

			{isRoleControlled && (
				<div className="mb-6">
					<InfoMessage message={dictionary.roleControlled} />
				</div>
			)}

			{/* General Permissions Table */}
			<div className="overflow-hidden rounded-xl shadow-lg border border-gray-100 bg-white mb-8">
				<div className="p-4 bg-gradient-to-r from-indigo-50 to-blue-50 border-b border-gray-200">
					<h3 className="text-lg font-semibold text-gray-800">
						{dictionary.generalPermissions}
					</h3>
				</div>
				<div className="overflow-x-auto">
					<div className="min-w-[600px]">
						<div className="bg-gray-50 p-5 grid grid-cols-5 gap-5 items-center">
							<div className="col-span-1" />
							{permissionHeaders.map(({ header }) => (
								<div key={header} className="col-span-1 text-center">
									<span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
										{header}
									</span>
								</div>
							))}
						</div>

						{/* Permission Rows */}
						{dataLevels.map(({ level, labelKey }) => (
							<div
								key={level}
								className="p-4 grid grid-cols-5 gap-5 border-b border-gray-100 last:border-b-0 items-center"
							>
								<div className="col-span-1">
									<span className="text-base font-medium text-gray-700">
										{labelKey}
									</span>
									{isRoleControlled && (
										<span className="ml-2 text-xs text-gray-500 italic">
											({dictionary.roleControlledItem})
										</span>
									)}
								</div>
								{permissionHeaders.map(({ type }) => {
									const currentFlag = getFlagKey(level, type);
									const isDisabled = isSwitchDisabled(currentFlag);

									return (
										<div
											key={`${level}-${type}`}
											className="col-span-1 flex items-center justify-center"
										>
											<Switch
												on={permissions[currentFlag]}
												onChange={() => handlePermissionChange(currentFlag)}
												disabled={isDisabled}
											/>
										</div>
									);
								})}
							</div>
						))}
					</div>
				</div>
			</div>

			{/* Additional Permissions Section */}
			<div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6 space-y-4 mb-8">
				<h3 className="text-lg font-semibold text-gray-800 mb-3">
					{dictionary.additionalPermissions}
				</h3>
				{[
					isCodelistEnabledForProject && {
						labelKey: dictionary.allowCodelistRead,
						flag: 'accessCodelistRead'
					},
					isCodelistEnabledForProject && {
						labelKey: dictionary.allowCodelistWrite,
						flag: 'accessCodelistWrite'
					},
					isCodelistEnabledForProject && {
						labelKey: dictionary.allowCodelistExport,
						flag: 'accessCodelistExport'
					},
					{ labelKey: dictionary.allowExport, flag: 'accessExport' },
					{ labelKey: dictionary.allowRestricted, flag: 'accessPersonalData' },
					{ labelKey: dictionary.allowEditVariables, flag: 'accessVariablesWrite' },
					{ labelKey: dictionary.allowEditProject, flag: 'accessProjectWrite' }
				]
					.filter(item => !!item)
					.map(({ labelKey, flag }) => {
						const currentFlag = flag as keyof PermissionsState;
						const isDisabled = isSwitchDisabled(currentFlag);

						return (
							<div
								key={flag}
								className="flex flex-row items-center justify-between py-2 border-b border-gray-100 last:border-b-0"
							>
								<div className="flex items-center">
									<span
										className={`text-base font-medium ${
											isDisabled ? 'text-gray-400' : 'text-gray-700'
										}`}
									>
										{labelKey}
									</span>
									{isRoleControlled && (
										<span className="ml-2 text-xs text-gray-500 italic">
											({dictionary.roleControlledItem})
										</span>
									)}
								</div>
								<Switch
									on={permissions[currentFlag]}
									onChange={() => handlePermissionChange(currentFlag)}
									disabled={isDisabled}
								/>
							</div>
						);
					})}
			</div>

			<InfoMessage message={dictionary.canModifyLater} />
		</div>
	);
};
